import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { DataList, PageHeader, StatusPill, type DataListColumn } from "@ozpanel/kit";
import { useAuth } from "@ozpanel/ui";

// Mail-enabled domains. Field names are the SERVED JSON keys, read from a live node rather
// than from the Go structs: /mail/domains returns
//   {items:[{domain, dkim_selector, active, mailboxes}], total}
// A shape guessed from Go renders the right headings with empty cells, which reads as "there
// is nothing here" instead of "this is the wrong build" (CLAUDE.md, the JSON-tag trap).
export interface MailDomain {
  domain: string;
  dkim_selector: string;
  active: boolean;
  mailboxes: number;
}

const columns: DataListColumn<MailDomain>[] = [
  {
    key: "domain",
    label: "Domain",
    sortable: true,
    render: (d) => (
      <Link className="text-accent hover:underline" to={`/mail/domains/${encodeURIComponent(d.domain)}`}>
        {d.domain}
      </Link>
    ),
  },
  {
    key: "active",
    label: "Mail",
    sortable: true,
    value: (d) => (d.active ? "active" : "off"),
    render: (d) => <StatusPill tone={d.active ? "ok" : "warn"}>{d.active ? "active" : "off"}</StatusPill>,
  },
  { key: "mailboxes", label: "Mailboxes", sortable: true, value: (d) => d.mailboxes ?? 0 },
  {
    key: "dkim_selector",
    label: "DKIM selector",
    sortable: true,
    // A domain with no selector has no signing key, which is not a cosmetic gap: its outbound
    // mail leaves unsigned and fails DMARC at the receiver. Say so here rather than showing a
    // blank cell that reads as "nothing to configure".
    render: (d) =>
      d.dkim_selector ? (
        <span className="font-mono text-[12px]">{d.dkim_selector}</span>
      ) : (
        <StatusPill tone="warn">no signing key</StatusPill>
      ),
  },
];

export function MailDomainsScreen() {
  const { client } = useAuth();
  const domains = useQuery({
    queryKey: ["mail-domains"],
    queryFn: () => client.apiFetch<{ items: MailDomain[] | null; total: number }>("/mail/domains"),
  });

  return (
    <div>
      <PageHeader
        title="Mail domains"
        subtitle={domains.data ? `${domains.data.total} mail-enabled` : undefined}
      />
      <DataList
        items={domains.data?.items ?? []}
        columns={columns}
        getRowKey={(d) => d.domain}
        searchable
        searchPlaceholder="Search mail domains…"
        loading={domains.isLoading}
        error={domains.error}
        errorContext="mail domains"
        emptyMessage="No domain has mail enabled."
        emptyHint="Mail is enabled on a domain that already exists under an account."
      />
    </div>
  );
}
